import { Button, Flex, Form, Input, Space, Typography } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { openNotification } from "../../../Components/Notification";
import { useLoader } from "../../../Context/LoaderContext";
import AuthService from "../../../Service/auth.service";
import { pushToDataLayer } from "../../../Utils/gtm";
import "./index.scss";

const SignInForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const authService = AuthService.getInstance();
  const { isLoading, setLoading, setLoadingMessage } = useLoader();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  const handleSignIn = async (formValues: any) => {
    try {
      setLoading(true);
      setLoadingMessage("Signing in please wait...");
      // Trim all string values and normalize email to lowercase
      const data = {
        email: formValues.email?.trim().toLowerCase(),
        password: formValues.password?.trim(),
      };

      // Track sign-in attempt
      pushToDataLayer({
        event: "signInAttempt",
        email: data.email,
        timestamp: new Date().toISOString(),
      });

      const response = await authService.signIn(data);
      if (response) {
        if (response.status === 500) {
          // Track failed sign-in
          pushToDataLayer({
            event: "signInFailed",
            reason: "account_not_exist",
            email: data.email,
            timestamp: new Date().toISOString(),
          });
          openNotification("error", "Account does not exist", "");
          return;
        } else if (response.status === 401) {
          // Track failed sign-in
          pushToDataLayer({
            event: "signInFailed",
            reason: "invalid_credentials",
            email: data.email,
            timestamp: new Date().toISOString(),
          });
          return;
        }
        if (response?.data?.access_token) {
          // Track successful sign-in
          pushToDataLayer({
            event: "signInSuccess",
            email: data.email,
            timestamp: new Date().toISOString(),
          });
          const url = redirect
            ? `/pre-load?redirect=${encodeURIComponent(redirect)}`
            : "/pre-load";
          navigate(url);
        } else {
          console.error("Invalid response from server");
        }
      }
    } catch (err: any) {
      // Check for timeout errors
      if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        pushToDataLayer({
          event: "signInError",
          error: "timeout",
          email: form.getFieldValue("email"),
          timestamp: new Date().toISOString(),
        });
        // Interceptor already shows message.error for timeout — no duplicate needed
      } else {
        // Track sign-in error
        pushToDataLayer({
          event: "signInError",
          error: err instanceof Error ? err.message : "Unknown error",
          timestamp: new Date().toISOString(),
        });
        console.error("Error while signing in user:", err);
      }
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  return (
    <Form
      form={form}
      name="normal_login"
      initialValues={{ remember: true }}
      onFinish={handleSignIn}
      autoComplete="off"
      scrollToFirstError
      className="sign-in-form"
    >
      <Form.Item
        name="email"
        rules={[
          {
            required: true,
            message: "Please enter your email!",
          },
          {
            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: "Please enter a valid email address!",
          },
        ]}
      >
        <Input placeholder="Email" />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          {
            required: true,
            message: "Please enter your password!",
          },
        ]}
      >
        <Input.Password placeholder="Password" />
      </Form.Item>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Flex align="center" justify="end">
          <Button
            type="link"
            onClick={() => navigate("/forgot-password")}
            className="forgot-password-link"
          >
            Forgot password?
          </Button>
        </Flex>
        <Button
          type="primary"
          htmlType="submit"
          block
          loading={isLoading}
          className="sign-in-button"
        >
          Sign In
        </Button>
        <Typography.Paragraph className="create-account-section">
          Don't have an account?
          <Button
            onClick={() =>
              navigate(
                `/sign-up?redirect=${encodeURIComponent(redirect || "/")}`
              )
            }
            type="link"
            className="create-account-link"
          >
            Create an account
          </Button>
        </Typography.Paragraph>
      </Space>
    </Form>
  );
};

export default SignInForm;
